/**
 * CustomerGrowthMap.jsx
 * Leaflet map — NO coordinates needed.
 * Keeps customer counts. Auto-geocodes via Nominatim (free OSM).
 *
 * npm install react-leaflet leaflet
 * Add to index.css: @import 'leaflet/dist/leaflet.css';
 */

import { useState, useEffect, useRef, useMemo, useCallback, useContext } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ChevronDown, X, MapPin, Loader2 } from "lucide-react";
import axios from "axios";
import { BASE_URL } from "../../../config/api";
import { AuthContext } from "../../../context/AuthContext";

// ─── Fix Leaflet default marker icons ────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ─── Bubble icon sized by customer count ─────────────────────────────────────
const dotIcon = (customers) => {
  const size = customers > 500 ? 36 : customers > 100 ? 28 : 20;
  const color =
    customers > 500 ? "rgba(29,78,216,0.9)"
    : customers > 100 ? "rgba(37,99,235,0.85)"
    : "rgba(96,165,250,0.85)";
  return L.divIcon({
    className: "",
    html: `<div style="
      width:${size}px;height:${size}px;
      background:${color};
      border:2.5px solid #fff;border-radius:50%;
      box-shadow:0 2px 8px rgba(37,99,235,0.4);
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-size:9px;font-weight:700;
    ">${customers > 9 ? customers : ""}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// ─── Geocode cache (module-level, survives re-renders) ────────────────────────
const geocodeCache = {};

async function geocode(query) {
  const key = query.trim().toLowerCase();
  if (geocodeCache[key]) return geocodeCache[key];
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query + ", India")}&format=json&limit=1`,
      { headers: { "Accept-Language": "en", "User-Agent": "WarrantyDashboard/1.0" } }
    );
    const data = await res.json();
    if (data?.[0]) {
      const result = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      geocodeCache[key] = result;
      return result;
    }
  } catch { /* silent */ }
  return null;
}

async function batchGeocode(queries) {
  const results = {};

  for (const q of queries.slice(0, 5)) { // 🔥 limit to 10 only
    const r = await geocode(q);
    if (r) results[q] = r;

    await new Promise((res) => setTimeout(res, 1200)); // 🔥 1.2 sec delay
  }

  return results;
}

// ─── Map helpers ──────────────────────────────────────────────────────────────
function InvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 150);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

function FlyTo({ center, zoom }) {
  const map = useMap();
  const prevKey = useRef(null);
  useEffect(() => {
    if (!center) return;
    const key = `${center[0]},${center[1]},${zoom}`;
    if (prevKey.current === key) return;
    prevKey.current = key;
    setTimeout(() => map.flyTo(center, zoom, { duration: 1.1 }), 100);
  }, [center?.[0], center?.[1], zoom]);
  return null;
}

// ─── Dropdown ─────────────────────────────────────────────────────────────────
function Select({ label, value, options, onChange, disabled, loading }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`flex items-center gap-1.5 text-xs border rounded-lg px-2.5 py-1.5 min-w-[115px] justify-between transition
          ${disabled
            ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
            : "bg-white text-slate-700 border-slate-200 hover:border-blue-400 shadow-sm cursor-pointer"
          }`}
      >
        <span className="truncate max-w-[80px] font-medium">{value || label}</span>
        {loading
          ? <Loader2 className="w-3 h-3 animate-spin text-blue-400 flex-shrink-0" />
          : value
          ? <X className="w-3 h-3 text-slate-400 flex-shrink-0"
               onClick={(e) => { e.stopPropagation(); onChange(null); }} />
          : <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" />
        }
      </button>
      {open && (
        <div className="absolute z-[9999] top-full mt-1 left-0 bg-white border border-slate-200 rounded-xl shadow-2xl min-w-[160px] max-h-56 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs transition
                ${value === opt ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700 hover:bg-blue-50 hover:text-blue-600"}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CustomerGrowthMap({dateRange}) {
  const { user: authUser } = useContext(AuthContext);
  const [selState, setSelState] = useState("MAHARASHTRA");
  const [selDistrict, setSelDistrict] = useState("THANE"); // or MUMBAI if exists
  const [selPincode,  setSelPincode]  = useState(null);

  // coords keyed by pincode string: { "411001": { lat, lng } }
  const [coords,    setCoords]    = useState({});
  const [geocoding, setGeocoding] = useState(false);
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
  const [mapZoom,   setMapZoom]   = useState(5);

  const [geoData, setGeoData] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const getQueryParams = () => {
  const role = authUser.role;
  const id = authUser.id;
  const companyId = authUser.company_id;

  let params = {};

  if (role == 2) {
    params.company_id = id;
  }

  if (role == 4) {
    params.company_id = companyId;
    params.agent_id = id;
  }

  return params;
};


useEffect(() => {
  const fetchGeoData = async () => {
    try {
      setLoadingData(true);

      const params = getQueryParams();

      const res = await axios.get(
        `${BASE_URL}warranty/geo-data`,
        { params }
      );

      if (res.data) {
        setGeoData(res.data);
      }

    } catch (err) {
      console.error("Geo API error:", err);
    } finally {
      setLoadingData(false);
    }
  };

  fetchGeoData();
}, []);


useEffect(() => {
  if (!geoData || !Object.keys(geoData).length) return;

  if (!geoData[selState]) {
    const firstState = Object.keys(geoData)[0];
    setSelState(firstState);

    const firstDistrict = Object.keys(geoData[firstState]?.districts || {})[0];
    setSelDistrict(firstDistrict);
  }
}, [geoData]);


useEffect(() => {
  if (!geoData || !geoData["MAHARASHTRA"]) return;

  const defaultDistrict = "THANE"; // change if needed

  const pins =
    geoData["MAHARASHTRA"]?.districts?.[defaultDistrict]?.pincodes || [];

  const pinCodes = (pins || []).map((p) => p.code);

  if (pinCodes.length) {
    fetchPins(pinCodes); // 🔥 only small set
  }
}, [geoData]);

  const states    = Object.keys(geoData);
  const districts = selState
  ? Object.keys(geoData[selState]?.districts || {})
  : [];
  const pincodes =
  selState && selDistrict
    ? geoData[selState]?.districts?.[selDistrict]?.pincodes?.map((p) => p.code) || []
    : [];

  // Pre-fetch coords for a list of pincodes (skips already cached)
  const fetchPins = useCallback(async (pins) => {
    const missing = pins.filter((p) => !geocodeCache[p.trim().toLowerCase()]);
    if (!missing.length) {
      // pull from module cache into state
      const fromCache = {};
      pins.forEach((p) => { const c = geocodeCache[p.trim().toLowerCase()]; if (c) fromCache[p] = c; });
      setCoords((prev) => ({ ...prev, ...fromCache }));
      return;
    }
    setGeocoding(true);
    try {
      const newCoords = await batchGeocode(missing);
      setCoords((prev) => ({ ...prev, ...newCoords }));
    } finally {
      setGeocoding(false);
    }
  }, []);

  // State selected
  const handleStateChange = async (s) => {
    setSelState(s);

    const firstDistrict = Object.keys(geoData[s]?.districts || {})[0];
    setSelDistrict(firstDistrict);
    setSelPincode(null);

    if (!firstDistrict) return;

    const pins =
  geoData[s]?.districts?.[firstDistrict]?.pincodes?.map((p) => p.code) || [];
    await fetchPins(pins);
  };

  // District selected
  const handleDistrictChange = async (d) => {
    setSelDistrict(d); setSelPincode(null);
    if (!d) return;
    setGeocoding(true);
    const center = await geocode(`${d}, ${selState}`);
    if (center) { setMapCenter([center.lat, center.lng]); setMapZoom(10); }
    const pins =
     geoData[selState]?.districts?.[d]?.pincodes?.map((p) => p.code) || [];
    await fetchPins(pins);
    setGeocoding(false);
  };

  // Pincode selected
  const handlePincodeChange = async (code) => {
    setSelPincode(code);
    if (!code) return;
    setGeocoding(true);
    const cached = geocodeCache[code.trim().toLowerCase()];
    const c = cached || (await geocode(code));
    if (c) {
      setCoords((prev) => ({ ...prev, [code]: c }));
      setMapCenter([c.lat, c.lng]);
      setMapZoom(13);
    }
    setGeocoding(false);
  };

  const handleReset = () => {
    setSelState(null); setSelDistrict(null); setSelPincode(null);
    setMapCenter([20.5937, 78.9629]); setMapZoom(5);
  };

  // Which pincode objects to render as markers
const visibleMarkers = useMemo(() => {
  let pins = [];

  if (selPincode && selDistrict) {
    pins = geoData[selState]?.districts[selDistrict]?.pincodes.filter(
      (p) => p.code === selPincode
    );
  } else if (selDistrict) {
    pins = geoData[selState]?.districts[selDistrict]?.pincodes || [];
  } else if (selState) {
    pins = Object.values(geoData[selState]?.districts || {}).flatMap(
      (d) => d.pincodes
    );
  } else {
    pins = Object.values(geoData).flatMap((s) =>
      Object.values(s.districts).flatMap((d) => d.pincodes)
    );
  }

  return pins;
}, [geoData, selState, selDistrict, selPincode, coords]);

// useEffect(() => {
//   const allPins = Object.values(geoData).flatMap((s) =>
//     Object.values(s.districts).flatMap((d) =>
//       d.pincodes.map((p) => p.code)
//     )
//   );

//   if (allPins.length) {
//     fetchPins(allPins);
//   }
// }, [geoData]);

//   const filteredMarkers = useMemo(() => {
//   if (!dateRange?.start || !dateRange?.end) return visibleMarkers;

//   return visibleMarkers.filter((m) => {
//     const d = new Date(m.date);
//     return d >= dateRange.start && d <= dateRange.end;
//   });
// }, [visibleMarkers, dateRange]);

  const filteredMarkers = visibleMarkers;

  const totalCustomers = filteredMarkers.reduce((s, m) => s + m.customers, 0);

  const breadcrumb = selPincode
    ? `Pincode ${selPincode} · ${selDistrict}, ${selState}`
    : selDistrict ? `${selDistrict}, ${selState}`
    : selState || "All India";

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">

      {/* Header */}
      <div className="px-5 pt-4 pb-3 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Customer Growth</h3>
            <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1.5">
              {breadcrumb} ·{" "}
              <span className="text-blue-600 font-semibold">{totalCustomers.toLocaleString()}</span> customers
              {geocoding && (
                <span className="flex items-center gap-1 text-blue-400 ml-1">
                  <Loader2 className="w-2.5 h-2.5 animate-spin" /> Locating…
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Select label="State"    value={selState}    options={states}    onChange={handleStateChange}    loading={geocoding && !selState} />
            <Select label="District" value={selDistrict} options={districts} onChange={handleDistrictChange} disabled={!selState}    loading={geocoding && !!selState && !selDistrict} />
            <Select label="Pincode"  value={selPincode}  options={pincodes}  onChange={handlePincodeChange}  disabled={!selDistrict} loading={geocoding && !!selDistrict && !selPincode} />
            {(selState || selDistrict || selPincode) && (
              <button
                onClick={handleReset}
                className="text-[11px] text-slate-400 hover:text-red-500 flex items-center gap-0.5 px-1.5 py-1 rounded hover:bg-red-50 transition"
              >
                <X className="w-3 h-3" /> Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div style={{ height: 320, width: "100%", position: "relative" }}>
        {geocoding && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-white/95 border border-blue-100 rounded-full px-3 py-1 flex items-center gap-1.5 shadow text-xs text-slate-600 pointer-events-none">
            <Loader2 className="w-3 h-3 animate-spin text-blue-500" /> Fetching locations…
          </div>
        )}
        <MapContainer
          center={[20.5937, 78.9629]}
          zoom={5}
          style={{ height: "100%", width: "100%", zIndex: 0 }}
          zoomControl
          scrollWheelZoom={false}
          attributionControl={false}
        >
          <InvalidateSize />
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FlyTo center={mapCenter} zoom={mapZoom} />

          {filteredMarkers.map((m) => {
            const c = coords[m.code];
            if (!c) return null;
            return (
              <Marker key={m.code} position={[c.lat, c.lng]} icon={dotIcon(m.customers)}>
                <Popup>
                  <div style={{ fontFamily: "DM Sans, sans-serif", minWidth: 120 }}>
                    <p style={{ fontWeight: 700, color: "#1e40af", marginBottom: 2, fontSize: 13 }}>
                      <MapPin style={{ display: "inline", width: 12, marginRight: 3 }} />
                      {m.label}
                    </p>
                    <p style={{ fontSize: 11, color: "#64748b", margin: 0 }}>Pincode: {m.code}</p>
                    <p style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", margin: "4px 0 0" }}>
                      {m.customers.toLocaleString()} customers
                    </p>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="w-3 h-3 rounded-full bg-blue-800 inline-block" /> High (&gt;500)
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block" /> Medium (100–500)
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" /> Low (&lt;100)
        </div>
        <span className="ml-auto text-[11px] text-slate-400">
          {geocoding ? "Loading pins…" : `${filteredMarkers.length} location${filteredMarkers.length !== 1 ? "s" : ""} shown`}
        </span>
      </div>
    </div>
  );
}

// /**
//  * CustomerGrowthMap.jsx
//  * Real interactive map (Leaflet + OpenStreetMap) with cascading
//  * State → District → Pincode filters.
//  *
//  * Install once:  npm install react-leaflet leaflet
//  * Add to your index.html / index.css:
//  *   <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
//  * OR import it at the top of your root CSS/JS:
//  *   import 'leaflet/dist/leaflet.css';
//  */

// import { useState, useEffect, useRef, useMemo } from "react";
// import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
// import L from "leaflet";
// import "leaflet/dist/leaflet.css";
// import { ChevronDown, X, MapPin } from "lucide-react";

// // ─── Fix Leaflet default marker icons in Webpack/Vite ───────────────────────
// delete L.Icon.Default.prototype._getIconUrl;
// L.Icon.Default.mergeOptions({
//   iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
//   iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
//   shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
// });

// // Custom blue dot icon
// const dotIcon = (count) =>
//   L.divIcon({
//     className: "",
//     html: `<div style="
//       width:${count > 100 ? 36 : count > 20 ? 28 : 20}px;
//       height:${count > 100 ? 36 : count > 20 ? 28 : 20}px;
//       background:rgba(37,99,235,0.85);
//       border:2.5px solid #fff;
//       border-radius:50%;
//       box-shadow:0 2px 8px rgba(37,99,235,0.4);
//       display:flex;align-items:center;justify-content:center;
//       color:#fff;font-size:9px;font-weight:700;
//     ">${count > 9 ? count : ""}</div>`,
//     iconSize: [count > 100 ? 36 : count > 20 ? 28 : 20, count > 100 ? 36 : count > 20 ? 28 : 20],
//     iconAnchor: [14, 14],
//   });

// // ─── Static geo data (India – extend as needed) ───────────────────────────
// // Each entry: state → districts → pincodes with lat/lng + customer count
// const GEO_DATA = {
//   Maharashtra: {
//     center: [19.7515, 75.7139],
//     zoom: 6,
//     districts: {
//       Pune: {
//         center: [18.5204, 73.8567],
//         zoom: 10,
//         pincodes: [
//           { code: "411001", lat: 18.5204, lng: 73.8567, customers: 489, label: "Pune City" },
//           { code: "411011", lat: 18.4941, lng: 73.8553, customers: 213, label: "Bibwewadi" },
//           { code: "411045", lat: 18.5893, lng: 73.7411, customers: 178, label: "Hinjewadi" },
//           { code: "411057", lat: 18.5642, lng: 73.9140, customers: 94, label: "Viman Nagar" },
//         ],
//       },
//       Mumbai: {
//         center: [19.0760, 72.8777],
//         zoom: 11,
//         pincodes: [
//           { code: "400001", lat: 18.9388, lng: 72.8354, customers: 712, label: "Fort" },
//           { code: "400050", lat: 19.0596, lng: 72.8295, customers: 534, label: "Bandra" },
//           { code: "400093", lat: 19.1764, lng: 72.9512, customers: 321, label: "Powai" },
//           { code: "400069", lat: 19.1075, lng: 72.8263, customers: 198, label: "Andheri" },
//         ],
//       },
//       Nashik: {
//         center: [20.0059, 73.7897],
//         zoom: 10,
//         pincodes: [
//           { code: "422001", lat: 20.0059, lng: 73.7897, customers: 143, label: "Nashik Road" },
//           { code: "422011", lat: 20.0333, lng: 73.8399, customers: 87, label: "Cidco" },
//         ],
//       },
//       Nagpur: {
//         center: [21.1458, 79.0882],
//         zoom: 10,
//         pincodes: [
//           { code: "440001", lat: 21.1458, lng: 79.0882, customers: 267, label: "Nagpur HQ" },
//           { code: "440022", lat: 21.1540, lng: 79.0537, customers: 119, label: "Sitabuldi" },
//         ],
//       },
//     },
//   },
//   Karnataka: {
//     center: [15.3173, 75.7139],
//     zoom: 6,
//     districts: {
//       Bengaluru: {
//         center: [12.9716, 77.5946],
//         zoom: 10,
//         pincodes: [
//           { code: "560001", lat: 12.9716, lng: 77.5946, customers: 856, label: "MG Road" },
//           { code: "560037", lat: 12.9279, lng: 77.6271, customers: 432, label: "Koramangala" },
//           { code: "560066", lat: 13.0358, lng: 77.5970, customers: 374, label: "Hebbal" },
//         ],
//       },
//       Mysuru: {
//         center: [12.2958, 76.6394],
//         zoom: 10,
//         pincodes: [
//           { code: "570001", lat: 12.2958, lng: 76.6394, customers: 198, label: "Mysuru City" },
//           { code: "570023", lat: 12.3119, lng: 76.6540, customers: 87, label: "Vijaynagar" },
//         ],
//       },
//     },
//   },
//   Gujarat: {
//     center: [22.2587, 71.1924],
//     zoom: 6,
//     districts: {
//       Ahmedabad: {
//         center: [23.0225, 72.5714],
//         zoom: 10,
//         pincodes: [
//           { code: "380001", lat: 23.0225, lng: 72.5714, customers: 543, label: "Ahmedabad City" },
//           { code: "380015", lat: 23.0395, lng: 72.5247, customers: 287, label: "Satellite" },
//         ],
//       },
//       Surat: {
//         center: [21.1702, 72.8311],
//         zoom: 10,
//         pincodes: [
//           { code: "395001", lat: 21.1702, lng: 72.8311, customers: 321, label: "Surat City" },
//           { code: "395007", lat: 21.2158, lng: 72.8372, customers: 156, label: "Adajan" },
//         ],
//       },
//     },
//   },
//   Delhi: {
//     center: [28.6139, 77.2090],
//     zoom: 10,
//     districts: {
//       "New Delhi": {
//         center: [28.6139, 77.2090],
//         zoom: 11,
//         pincodes: [
//           { code: "110001", lat: 28.6139, lng: 77.2090, customers: 934, label: "Connaught Place" },
//           { code: "110020", lat: 28.5245, lng: 77.1855, customers: 445, label: "Vasant Kunj" },
//           { code: "110092", lat: 28.6862, lng: 77.3032, customers: 312, label: "Shahdara" },
//         ],
//       },
//       "South Delhi": {
//         center: [28.5494, 77.2001],
//         zoom: 11,
//         pincodes: [
//           { code: "110048", lat: 28.5494, lng: 77.2001, customers: 578, label: "Greater Kailash" },
//           { code: "110076", lat: 28.5128, lng: 77.2773, customers: 289, label: "Okhla" },
//         ],
//       },
//     },
//   },
// };

// // ─── Invalidate map size on mount (fixes split-tile bug) ─────────────────────
// function InvalidateSize() {
//   const map = useMap();
//   useEffect(() => {
//     const t = setTimeout(() => map.invalidateSize(), 100);
//     return () => clearTimeout(t);
//   }, [map]);
//   return null;
// }

// // ─── Map fly-to helper ────────────────────────────────────────────────────────
// function FlyTo({ center, zoom }) {
//   const map = useMap();
//   useEffect(() => {
//     if (center) {
//       setTimeout(() => map.flyTo(center, zoom, { duration: 1.2 }), 150);
//     }
//   }, [center, zoom]);
//   return null;
// }

// // ─── Dropdown ────────────────────────────────────────────────────────────────
// function Select({ label, value, options, onChange, disabled }) {
//   const [open, setOpen] = useState(false);
//   const ref = useRef();

//   useEffect(() => {
//     const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
//     document.addEventListener("mousedown", handler);
//     return () => document.removeEventListener("mousedown", handler);
//   }, []);

//   return (
//     <div className="relative" ref={ref}>
//       <button
//         disabled={disabled}
//         onClick={() => !disabled && setOpen((o) => !o)}
//         className={`flex items-center gap-1.5 text-xs border rounded-lg px-2.5 py-1.5 min-w-[110px] justify-between transition
//           ${disabled ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed" : "bg-white text-slate-700 border-slate-200 hover:border-blue-300 cursor-pointer shadow-sm"}`}
//       >
//         <span className="truncate max-w-[80px]">{value || label}</span>
//         {value ? (
//           <X className="w-3 h-3 text-slate-400 flex-shrink-0" onClick={(e) => { e.stopPropagation(); onChange(null); }} />
//         ) : (
//           <ChevronDown className="w-3 h-3 flex-shrink-0" />
//         )}
//       </button>
//       {open && (
//         <div className="absolute z-[9999] top-full mt-1 left-0 bg-white border border-slate-200 rounded-xl shadow-xl min-w-[160px] max-h-52 overflow-y-auto">
//           {options.map((opt) => (
//             <button
//               key={opt}
//               onClick={() => { onChange(opt); setOpen(false); }}
//               className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 hover:text-blue-700 transition
//                 ${value === opt ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700"}`}
//             >
//               {opt}
//             </button>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// }

// // ─── Main Component ───────────────────────────────────────────────────────────
// export default function CustomerGrowthMap() {
//   const [selState, setSelState] = useState(null);
//   const [selDistrict, setSelDistrict] = useState(null);
//   const [selPincode, setSelPincode] = useState(null);

//   const states = Object.keys(GEO_DATA);
//   const districts = selState ? Object.keys(GEO_DATA[selState].districts) : [];
//   const pincodes = selState && selDistrict
//     ? GEO_DATA[selState].districts[selDistrict].pincodes.map((p) => p.code)
//     : [];

//   // When state changes, reset children
//   const handleStateChange = (s) => { setSelState(s); setSelDistrict(null); setSelPincode(null); };
//   const handleDistrictChange = (d) => { setSelDistrict(d); setSelPincode(null); };

//   // Compute map center + zoom
//   const { center, zoom, markers } = useMemo(() => {
//     if (selState && selDistrict && selPincode) {
//       const pin = GEO_DATA[selState].districts[selDistrict].pincodes.find((p) => p.code === selPincode);
//       return { center: [pin.lat, pin.lng], zoom: 13, markers: [pin] };
//     }
//     if (selState && selDistrict) {
//       const d = GEO_DATA[selState].districts[selDistrict];
//       return { center: d.center, zoom: d.zoom, markers: d.pincodes };
//     }
//     if (selState) {
//       const s = GEO_DATA[selState];
//       const allPins = Object.values(s.districts).flatMap((d) => d.pincodes);
//       return { center: s.center, zoom: s.zoom, markers: allPins };
//     }
//     // India default
//     const allPins = Object.values(GEO_DATA).flatMap((s) => Object.values(s.districts).flatMap((d) => d.pincodes));
//     return { center: [20.5937, 78.9629], zoom: 5, markers: allPins };
//   }, [selState, selDistrict, selPincode]);

//   const totalCustomers = markers.reduce((s, m) => s + m.customers, 0);

//   return (
//     <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
//       {/* Header */}
//       <div className="px-5 pt-4 pb-3 border-b border-slate-100">
//         <div className="flex items-start justify-between gap-3 flex-wrap">
//           <div>
//             <h3 className="text-sm font-semibold text-slate-800">Customer Growth</h3>
//             <p className="text-[11px] text-slate-400 mt-0.5">
//               {selPincode
//                 ? `Pincode ${selPincode}`
//                 : selDistrict
//                 ? `${selDistrict}, ${selState}`
//                 : selState
//                 ? selState
//                 : "All India"}{" "}
//               · <span className="text-blue-600 font-semibold">{totalCustomers.toLocaleString()}</span> customers
//             </p>
//           </div>
//           {/* Filters */}
//           <div className="flex items-center gap-2 flex-wrap">
//             <Select
//               label="State"
//               value={selState}
//               options={states}
//               onChange={handleStateChange}
//             />
//             <Select
//               label="District"
//               value={selDistrict}
//               options={districts}
//               onChange={handleDistrictChange}
//               disabled={!selState}
//             />
//             <Select
//               label="Pincode"
//               value={selPincode}
//               options={pincodes}
//               onChange={setSelPincode}
//               disabled={!selDistrict}
//             />
//             {(selState || selDistrict || selPincode) && (
//               <button
//                 onClick={() => { setSelState(null); setSelDistrict(null); setSelPincode(null); }}
//                 className="text-[11px] text-slate-400 hover:text-red-500 transition flex items-center gap-0.5"
//               >
//                 <X className="w-3 h-3" /> Reset
//               </button>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Map */}
//       <div style={{ height: 320, width: "100%", position: "relative" }}>
//         <MapContainer
//           center={[20.5937, 78.9629]}
//           zoom={5}
//           style={{ height: "100%", width: "100%", zIndex: 0 }}
//           zoomControl={true}
//           scrollWheelZoom={false}
//           attributionControl={false}
//           whenCreated={(map) => setTimeout(() => map.invalidateSize(), 200)}
//         >
//           <InvalidateSize />
//           <TileLayer
//             url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//             attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
//           />
//           <FlyTo center={center} zoom={zoom} />
//           {markers.map((m) => (
//             <Marker key={m.code} position={[m.lat, m.lng]} icon={dotIcon(m.customers)}>
//               <Popup>
//                 <div style={{ fontFamily: "DM Sans, sans-serif", minWidth: 120 }}>
//                   <p style={{ fontWeight: 700, color: "#1e40af", marginBottom: 2, fontSize: 13 }}>
//                     <MapPin style={{ display: "inline", width: 12, marginRight: 3 }} />
//                     {m.label}
//                   </p>
//                   <p style={{ fontSize: 11, color: "#64748b", margin: 0 }}>Pincode: {m.code}</p>
//                   <p style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", margin: "4px 0 0" }}>
//                     {m.customers.toLocaleString()} customers
//                   </p>
//                 </div>
//               </Popup>
//             </Marker>
//           ))}
//         </MapContainer>
//       </div>

//       {/* Legend / Stats row */}
//       <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-4 flex-wrap">
//         <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
//           <span className="w-3 h-3 rounded-full bg-blue-600 inline-block" /> High (&gt;200)
//         </div>
//         <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
//           <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" /> Medium (20–200)
//         </div>
//         <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
//           <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" /> Low (&lt;20)
//         </div>
//         <span className="ml-auto text-[11px] text-slate-400">
//           {markers.length} location{markers.length !== 1 ? "s" : ""} shown
//         </span>
//       </div>
//     </div>
//   );
// }